/**
 * LifeEventItem Component
 * Single Responsibility: Display individual life event with actions
 */

import React, { useState } from 'react'
import {
  Heart,
  Baby,
  Briefcase,
  GraduationCap,
  Activity,
  TrendingUp,
  Home,
  DollarSign,
  Circle,
  HeartCrack,
  Calendar,
  Edit2,
  Trash2
} from 'lucide-react'
import { LifeEvent } from '../../../types/meeting'

interface LifeEventItemProps {
  event: LifeEvent
  // Optional so read-only surfaces (the canvas Life-Events widget, TASK-576) can
  // reuse this renderer without wiring edit/delete — the action buttons only show
  // when a handler is supplied.
  onEdit?: (event: LifeEvent) => void
  onDelete?: (eventId: string) => void
}

const LifeEventItem: React.FC<LifeEventItemProps> = ({
  event,
  onEdit,
  onDelete
}) => {
  const [showActions, setShowActions] = useState(false)

  // Map event types to icons
  const getEventIcon = (type: LifeEvent['type']) => {
    const iconProps = { className: "w-5 h-5" }

    switch (type) {
      case 'marriage':
        return <Heart {...iconProps} className="w-5 h-5 text-pink-500" />
      case 'divorce':
        return <HeartCrack {...iconProps} className="w-5 h-5 text-gray-500" />
      case 'birth':
        return <Baby {...iconProps} className="w-5 h-5 text-blue-500" />
      case 'death':
        return <Circle {...iconProps} className="w-5 h-5 text-gray-700" />
      case 'retirement':
        return <Briefcase {...iconProps} className="w-5 h-5 text-purple-500" />
      case 'education':
        return <GraduationCap {...iconProps} className="w-5 h-5 text-indigo-500" />
      case 'health':
        return <Activity {...iconProps} className="w-5 h-5 text-green-500" />
      case 'career':
        return <TrendingUp {...iconProps} className="w-5 h-5 text-blue-600" />
      case 'relocation':
        return <Home {...iconProps} className="w-5 h-5 text-orange-500" />
      case 'inheritance':
        return <DollarSign {...iconProps} className="w-5 h-5 text-yellow-600" />
      case 'business_sale':
        return <Briefcase {...iconProps} className="w-5 h-5 text-emerald-600" />
      case 'liquidity_event':
        return <TrendingUp {...iconProps} className="w-5 h-5 text-emerald-500" />
      default:
        return <Calendar {...iconProps} className="w-5 h-5 text-slate-500" />
    }
  }

  // Format event type for display: "business_sale" -> "Business Sale".
  const formatEventType = (type: LifeEvent['type']) => {
    return type
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  }

  // Format date for display. Life-event dates are often fuzzy ("2027", "next
  // year", "Unknown") — fall back to the raw string rather than "Invalid Date".
  const formatDate = (dateStr: string) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return dateStr
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Get financial impact color
  const getImpactColor = (impact?: LifeEvent['financialImpact']) => {
    switch (impact) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200'
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      case 'low':
        return 'bg-green-100 text-green-700 border-green-200'
      case 'none':
        return 'bg-slate-100 text-slate-700 border-slate-200'
      default:
        return ''
    }
  }

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete this life event?\n\n"${event.description}"`)) {
      onDelete?.(event.id)
    }
  }

  return (
    <div
      className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-all relative"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Event Icon */}
      <div className="flex-shrink-0 mt-0.5">
        {getEventIcon(event.type)}
      </div>

      {/* Event Content */}
      <div className="flex-grow">
        <div className="flex items-start justify-between">
          <div className="flex-grow">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-slate-700">
                {formatEventType(event.type)}
              </span>
              <span className="text-xs text-slate-500">
                {formatDate(event.date)}
              </span>
              {event.source === 'transcription' && event.confidenceScore && (
                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                  {Math.round(event.confidenceScore * 100)}% confidence
                </span>
              )}
            </div>
            <p className="text-sm text-slate-600">
              {event.description}
            </p>

            {/* Timeline information */}
            {event.timeline && (
              <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
                <Calendar className="w-3 h-3" />
                <span className="capitalize">{event.timeline.type || 'future'}</span>
                {event.timeline.date && <span>• {event.timeline.date}</span>}
                {event.timeline.certainty && (
                  <span className="text-slate-400">({event.timeline.certainty})</span>
                )}
              </div>
            )}

            {/* Financial impact (new structured format) */}
            {event.financial_impact && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs mt-2">
                <div className="font-medium text-amber-800">
                  Financial Impact: {event.financial_impact.level?.toUpperCase() || 'MEDIUM'}
                </div>
                {event.financial_impact.estimated_amount && (
                  <div className="text-amber-700 mt-1">
                    💰 {event.financial_impact.estimated_amount}
                  </div>
                )}
                {event.financial_impact.impact_areas && event.financial_impact.impact_areas.length > 0 && (
                  <div className="text-amber-600 mt-1">
                    Areas: {event.financial_impact.impact_areas.join(', ')}
                  </div>
                )}
              </div>
            )}

            {/* Legacy financial impact (backward compatibility) */}
            {!event.financial_impact && event.financialImpact && (
              <div className="mt-2">
                <span className={`inline-flex px-2 py-0.5 text-xs rounded-full border ${getImpactColor(event.financialImpact)}`}>
                  Financial Impact: {event.financialImpact}
                </span>
              </div>
            )}

            {/* Related services */}
            {event.related_services && event.related_services.length > 0 && (
              <div className="text-xs text-slate-400 mt-2">
                <span className="font-medium">Related Services:</span>{' '}
                {event.related_services.join(', ')}
              </div>
            )}
          </div>

          {/* Action Buttons (only when handlers are provided) */}
          {showActions && (onEdit || onDelete) && (
            <div className="flex items-center gap-1 ml-2">
              {onEdit && (
                <button
                  onClick={() => onEdit(event)}
                  className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                  title="Edit event"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={handleDelete}
                  className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                  title="Delete event"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LifeEventItem